/**
 * Environment configuration utility
 *
 * This utility provides a consistent way to access environment variables
 * whether running in development (process.env) or production (runtime config).
 *
 * In Cloud Run deployments, environment variables are injected at runtime
 * via the docker-entrypoint.sh script which creates window.RUNTIME_CONFIG.
 */

// Extend Window interface to include RUNTIME_CONFIG
declare global {
  interface Window {
    RUNTIME_CONFIG?: {
      [key: string]: string | undefined;
    };
  }
}

/**
 * Get environment variable value
 * Checks runtime config first (for Cloud Run), then falls back to process.env
 */
export function getEnv(key: string): string | undefined {
  // Check runtime config first (available in production/staging)
  if (typeof window !== 'undefined' && window.RUNTIME_CONFIG) {
    const value = window.RUNTIME_CONFIG[key];
    if (value !== undefined) {
      return value;
    }
  }

  // Fall back to process.env (available during build and in development)
  return process.env[key];
}

/**
 * Get environment variable with a default value
 */
export function getEnvOrDefault(key: string, defaultValue: string): string {
  return getEnv(key) || defaultValue;
}

// Export commonly used environment variables
// Using getters ensures values are evaluated each time they're accessed
export const ENV = {
  // App Configuration
  get API_URL() { return getEnvOrDefault('REACT_APP_API_URL', 'http://localhost:3000'); },
  get API_TIMEOUT() { return getEnvOrDefault('REACT_APP_API_TIMEOUT', '30000'); },
  get APP_ENV() { return getEnv('REACT_APP_ENV'); },

  // Firebase Configuration
  get FIREBASE_API_KEY() { return getEnv('REACT_APP_FIREBASE_API_KEY'); },
  get FIREBASE_AUTH_DOMAIN() { return getEnv('REACT_APP_FIREBASE_AUTH_DOMAIN'); },
  get FIREBASE_PROJECT_ID() { return getEnv('REACT_APP_FIREBASE_PROJECT_ID'); },
  get FIREBASE_STORAGE_BUCKET() { return getEnv('REACT_APP_FIREBASE_STORAGE_BUCKET'); },
  get FIREBASE_MESSAGING_SENDER_ID() { return getEnv('REACT_APP_FIREBASE_MESSAGING_SENDER_ID'); },
  get FIREBASE_APP_ID() { return getEnv('REACT_APP_FIREBASE_APP_ID'); },
  get FIREBASE_MEASUREMENT_ID() { return getEnv('REACT_APP_FIREBASE_MEASUREMENT_ID'); },

  // reCAPTCHA Configuration
  get RECAPTCHA_SITE_KEY() { return getEnv('REACT_APP_RECAPTCHA_SITE_KEY'); },

  // Stripe Configuration
  get STRIPE_PUBLISHABLE_KEY() { return getEnv('REACT_APP_STRIPE_PUBLISHABLE_KEY'); },
  get STRIPE_PRICE_ID_PRO() { return getEnv('REACT_APP_STRIPE_PRICE_ID_PRO'); },
  get STRIPE_PRICE_ID_ULTRA() { return getEnv('REACT_APP_STRIPE_PRICE_ID_ULTRA'); },
};
